import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MDXEditor } from './MDXEditor';
import {
  BoldItalicUnderlineToggles,
  diffSourcePlugin,
  headingsPlugin,
  listsPlugin,
  ListsToggle,
  MDXEditorMethods,
  MDXEditorProps,
  quotePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
} from '@mdxeditor/editor';
import { Label, LabelProps } from './Label';
import { DataTypesEnum, ObservableLevelEnumValue } from '../../data/enums';
import { observationService } from '../../services/observationService';
import { useObservationField } from '../../hooks/useObservationField';
import { validateInput } from './SingleLineTextField';
import RichTextFieldHelpers from '../../helpers/richTextFieldHelpers';
import { m } from 'framer-motion';

interface RichTextEditorProps {
  label?: string;
  onChange?: (value: string) => void; // called with the FULL serialized value
  onDefaultLoad?: (value: string) => void; // called with the FULL serialized value on initial load
  path: string;
  dataLevel: ObservableLevelEnumValue;
  entityId: number;
  entityExternalId: string;
  inspectionId: string;
  inspectionCategoryTypeId: string;
  tooltipKey?: string;
  labelProps?: Omit<LabelProps, 'tooltip' | 'children'>;
  primaryLocationId?: number; // when provided, show only this location’s block
  className?: string;
  placeholder?: string;
}

export const RichTextField = ({
  label,
  onChange,
  path,
  dataLevel,
  inspectionCategoryTypeId,
  inspectionId,
  entityId,
  entityExternalId,
  onDefaultLoad,
  className,
  labelProps,
  placeholder = 'Enter your comments here...',
  primaryLocationId,
  ...props
}: RichTextEditorProps &
  Omit<MDXEditorProps & React.RefAttributes<MDXEditorMethods>, 'markdown'>) => {
  const mdxRef = useRef<MDXEditorMethods>(null);

  // All locations content, keyed by locationId as string
  const [summaries, setSummaries] = useState<Record<string, string>>({});
  const summariesRef = useRef<Record<string, string>>({});
  useEffect(() => {
    summariesRef.current = summaries;
  }, [summaries]);

  // The content currently shown in the editor (just for the active location)
  const [localValue, setLocalValue] = useState<string>('');

  // Draft form layer: inside an ObservationDraftScope the serialized value lives in
  // the draft store. Every keystroke stores the PERSISTED format (the same transform
  // chain the blur-save runs) so a debounced flush mid-edit writes valid content.
  const draft = useObservationField({
    inspectionId,
    inspectionCategoryId: inspectionCategoryTypeId,
    dataLevel,
    entityId,
    entityExternalId,
    path,
  });
  const isDraft = draft.mode === 'draft';

  // Load once
  useEffect(() => {
    if (isDraft && !(draft.mode === 'draft' && draft.ready)) {
      return; // wait for the draft seed; the [draft.ready] effect below re-runs this
    }
    const getDefaultValue = async () => {
      let fullValue = '';
      if (isDraft) {
        fullValue = draft.mode === 'draft' ? draft.value : '';
      } else {
        const observation = await observationService.getObservation({
          dataLevel,
          entityId,
          entityExternalId,
          inspectionId,
          inspectionCategoryId: inspectionCategoryTypeId,
          path,
        });

        if (observation) {
          fullValue =
            observationService.convertValue<string>(
              observation.value,
              DataTypesEnum.string,
            ) ?? '';
        }
      }

      const parsed = RichTextFieldHelpers.parseSummaries(fullValue);

      // Ensure the active location key exists if primaryLocationId provided
      if (primaryLocationId != null) {
        const key = String(primaryLocationId);
        if (!(key in parsed)) {
          parsed[key] = ''; // start empty block for this location
        }
      }

      setSummaries(parsed);
      const initialLocal =
        primaryLocationId != null
          ? (parsed[String(primaryLocationId)] ?? '')
          : fullValue; // if no primaryLocationId, show everything

      const transformedMarkdown =
        RichTextFieldHelpers.transformToEditorMarkdown(initialLocal ?? '');

      setLocalValue(transformedMarkdown);
      mdxRef.current?.setMarkdown(transformedMarkdown);

      // Inform parent of FULL current value
      const serialized =
        primaryLocationId != null
          ? RichTextFieldHelpers.serializeSummaries(parsed)
          : initialLocal;
      onDefaultLoad?.(serialized);
    };

    getDefaultValue();
    // Draft mode re-runs once the seed lands (ready flips false -> true exactly once).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDraft && draft.mode === 'draft' && draft.ready]);

  // Keep the live local value in sync with the map while typing
  const handleChange = (content: string) => {
    if (!validateInput(content, 'string')) return;

    setLocalValue(content);

    // If we're scoping by location, update only that entry in the map in memory
    if (primaryLocationId != null) {
      const key = String(primaryLocationId);
      const next = { ...summariesRef.current, [key]: content };
      setSummaries(next);

      // Push FULL serialized value to parent for live consumers (optional)
      const full = RichTextFieldHelpers.serializeSummaries(next);
      if (isDraft) {
        // Store the persisted format on every change — a debounced flush can land mid-edit.
        draft.setValue(RichTextFieldHelpers.parseMarkdownCustom(full));
      }
      onChange?.(full);
    } else {
      // No primary location: the entire field is the content
      if (isDraft) {
        draft.setValue(RichTextFieldHelpers.parseMarkdownCustom(content));
      }
      onChange?.(content);
    }
  };

  const handleBlur = async (e: FocusEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null;

    if (
      relatedTarget &&
      relatedTarget.className.includes('_toolbarToggleItem_')
    ) {
      // Focus is moving to a toolbar button, do not save yet
      return;
    }

    const toSave =
      primaryLocationId != null
        ? RichTextFieldHelpers.serializeSummaries({
            ...summariesRef.current,
            [String(primaryLocationId)]: localValue,
          })
        : localValue;

    const parsedValue = RichTextFieldHelpers.parseMarkdownCustom(toSave);

    // Re-display only the active location's block in the editor. Using the full
    // serialized value here would surface the #<id># block markers and other
    // locations' text in the textarea until the next remount.
    const displayValue =
      primaryLocationId != null
        ? RichTextFieldHelpers.parseMarkdownCustom(localValue)
        : parsedValue;
    mdxRef.current?.setMarkdown(
      RichTextFieldHelpers.transformToEditorMarkdown(displayValue),
    );

    if (isDraft) {
      // The draft store already holds the persisted value (updated per change);
      // ensure blur-only edits (e.g. toolbar-applied formatting) are captured too.
      draft.setValue(parsedValue);
      onChange?.(parsedValue);
      return;
    }

    const response = await observationService.upsertObservation({
      dataLevel,
      entityId,
      entityExternalId,
      inspectionCategoryId: inspectionCategoryTypeId,
      inspectionId,
      path,
      value: parsedValue,
    });

    if (response !== '') {
      onChange?.(parsedValue);
    }
  };

  // Compute the markdown prop to pass to MDXEditor
  const markdown = useMemo(() => {
    return localValue ?? '';
  }, [localValue]);

  return (
    <div className={`space-y-4 ${className ?? ''}`}>
      {label && (
        <Label titleType="fieldTitle" {...labelProps} tooltipKey={'comments'}>
          {label}
        </Label>
      )}
      <div
        className={`overflow-hidden rounded-md border border-line focus:border-brand focus:ring-1 focus:ring-brand focus:outline-none`}
      >
        <MDXEditor
          markdown={markdown}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          {...props}
          ref={mdxRef}
        />
      </div>
    </div>
  );
};
